import { createContext, useContext, useEffect, useState } from 'react';

import { useSubscribeToAiDraftEvents } from './aiDraftEvents';

export type AiDraftActivityLogProps = {
  /** The suggestion whose history to show. */
  aiDraftId: string;
  /**
   * Goes up whenever the draft is known to have changed, so the log reads its
   * history again rather than showing what it had when it opened.
   */
  revision: number;
};

/** Renders a suggestion's activity log. */
export type AiDraftActivityLogRenderer = (
  props: AiDraftActivityLogProps,
) => JSX.Element;

const AiDraftActivityLogContext = createContext<
  AiDraftActivityLogRenderer | undefined
>(undefined);

/**
 * Supplied by the host app, which alone can read the activity log. Injected
 * via the `renderAiDraftActivityLog` prop on the knowledge management entry.
 * Without it the suggestion preview offers no activity log.
 */
export const AiDraftActivityLogProvider = AiDraftActivityLogContext.Provider;

export function useAiDraftActivityLogRenderer():
  | AiDraftActivityLogRenderer
  | undefined {
  return useContext(AiDraftActivityLogContext);
}

/**
 * Counts the changes the web socket reports for one draft, and reconnects,
 * after which anything could have changed. Without the host's events it stays
 * at zero and the log is the snapshot it was when it opened.
 */
export function useAiDraftRevision(aiDraftId: string): number {
  const subscribe = useSubscribeToAiDraftEvents();
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!subscribe) return undefined;
    const bump = () => setRevision((current) => current + 1);
    return subscribe({
      onUpserted: (id) => {
        if (id === aiDraftId) bump();
      },
      // A deleted draft leaves the preview, and its log with it.
      onDeleted: () => undefined,
      onReconnected: bump,
    });
  }, [subscribe, aiDraftId]);

  return revision;
}
